import { Injectable } from '@angular/core';

import { environment } from '../environments/environment';
import {
    IField, IErrorNotification, ILanguageItem,
    IPaymentProduct, IObjectWithId, IProfilePageResponse,
    ICartItemProduct
} from './shared/interfaces';
import { SearchParamName, ServerErrorCode, TranslationMapKey } from './enums';
import { IProductVisibility } from './shared/interfaces';
import { IDependencyWithResult, IIdWithValue, IMergeParticipantWithUtmObjectResult } from './interfaces';
import { UtilsService } from './shared/utils.service';
import { SharedService } from './shared/shared.service';
import { CartItem } from '@azavista/payments';

@Injectable({
    providedIn: 'root'
})
export class AppService {

    constructor(
        private utilsSvc: UtilsService,
        private readonly sharedSvc: SharedService
    ) {
    }

    mergeHtmlFormDataWithUtmObject(
        htmlFormData: Record<string, any>,
        utmParamValue: Record<string, any>,
        mergeParticipantWithUtmObjectResult: IMergeParticipantWithUtmObjectResult
    ): Record<string, any> {
        if (!utmParamValue) {
            return htmlFormData;
        }
        const result: Record<string, any> = { ...htmlFormData };
        const originalParticipant = mergeParticipantWithUtmObjectResult.originalParticipantObject;
        const utmParamValueKeys = Object.keys(utmParamValue);
        const htmlFormDataKeys = Object.keys(htmlFormData);
        for (const key of utmParamValueKeys) {
            if (!htmlFormDataKeys.includes(key) && (originalParticipant[key] === null || originalParticipant[key] === undefined)) {
                // The html form data does not include the utm property and original participant also does not have it
                // We need to add it to the html form object so it is saved
                result[key] = utmParamValue[key];
            }
        }
        return result;
    }

    mergeParticipantWithUtmObject(eventParticipant: IObjectWithId, utmObject: Record<string, any>): IMergeParticipantWithUtmObjectResult {
        const originalParticipantObject = { ...eventParticipant };
        const resultingObject: IObjectWithId = { ...eventParticipant };
        const mergedUtmObject: Record<string, any> = {};
        if (!utmObject) {
            return { originalParticipantObject, resultingObject, mergedUtmObject };
        }
        const utmKeys = Object.keys(utmObject);
        for (const key of utmKeys) {
            // Empty string and value 0 are valid values and we are not overriding them
            if (resultingObject[key] === null || resultingObject[key] === undefined) {
                const utmObjectValue = utmObject[key];
                resultingObject[key] = utmObjectValue;
                mergedUtmObject[key] = utmObjectValue;
            }
        }
        return { originalParticipantObject, resultingObject, mergedUtmObject };
    }

    getUtmParamValueWithSubstitutedFieldIds(eventParticipantFields: IField[], originalUtmParamObject: Record<string, any>): Record<string, any> {
        if (!originalUtmParamObject) {
            return null;
        }
        // The utm query parameter object could contain field ids and also crm field ids
        // We need to find the real field id if crm field id is provided in the utm param object
        const result: Record<string, any> = {};
        const utmKeys = Object.keys(originalUtmParamObject);
        for (const utmKey of utmKeys) {
            const field = eventParticipantFields.find(x => x.id === utmKey || x.crm_field_id === utmKey);
            const foundFieldId = field?.id ?? utmKey;
            result[foundFieldId] = originalUtmParamObject[utmKey];
        }
        return result;
    }

    getProductVisibilitiesByDependencyResults(dependencyResults: IDependencyWithResult[], products: IPaymentProduct[]): IProductVisibility[] {
        const productsVisibility: IProductVisibility[] = [];
        for (const depResult of dependencyResults) {
            for (const product of products) {
                const productDependenciesIds = product.dependency_ids || [product.dependency_id].filter(x => !!x);
                for (const productDepId of productDependenciesIds) {
                    if (productDepId === depResult.dependency.id) {
                        productsVisibility.push({ hidden: !depResult.result, productId: product.id });
                    }
                }
            }
        }
        // Set products to visible if they have at least one "hidden: false"
        const productsVisibilityGroupedByProduct = this.sharedSvc.getGroups(productsVisibility, x => x.productId);
        const filteredProductsVisibility: IProductVisibility[] = [];
        for (const grp of productsVisibilityGroupedByProduct) {
            const notHiddenItem = grp.items.find(x => !x.hidden);
            if (notHiddenItem) {
                // There is not hidden item - add it to result
                filteredProductsVisibility.push(notHiddenItem);
            } else {
                // All items are hidden - just get first of the group
                filteredProductsVisibility.push(grp.items[0]);
            }
        }

        return filteredProductsVisibility;
    }

    setMissingParticipantFields(
        eventParticipant: IObjectWithId, eventParticipantFields: IField[], profilePageResponse: IProfilePageResponse
    ): void {
        if (!profilePageResponse?.fields || !profilePageResponse?.contact) {
            return;
        }
        // Find fields in profile page (contact fields) that correspond by name to event participant fields
        for (const profileField of profilePageResponse.fields) {
            const eventParticipantField = eventParticipantFields.find(x => x.name === profileField.name);
            if (eventParticipantField) {
                if (!eventParticipant[eventParticipantField.id]) {
                    // Event participant does not have value for this field - try to get it from profile page
                    const profilePageFieldValue = profilePageResponse.contact[profileField.id];
                    if (profilePageFieldValue) {
                        eventParticipant[eventParticipantField.id] = profilePageFieldValue;
                    }
                }
            }
        }
    }

    translate(key: TranslationMapKey): string {
        return this.sharedSvc.translate(key);
    }

    getIntersectionArray<T>(arr1: T[], arr2: T[]): T[] {
        const set2 = new Set(arr2);
        return [...new Set(arr1)].filter(x => set2.has(x));
    }

    getModifiedEventParticipantFields(fields: IField[], eventLanguages: ILanguageItem[]): IField[] {
        const modifiedFields: IField[] = [];
        for (const field of fields) {
            if (this.isPreferredLanguageField(field)) {
                const modifiedPreferredLanguageField = { ...field };
                const fieldItems = modifiedPreferredLanguageField.schema.enum;
                const eventLanguageKeys = eventLanguages.map(x => x.id);
                modifiedPreferredLanguageField.schema.enum = this.getIntersectionArray(eventLanguageKeys, fieldItems);
                modifiedFields.push(modifiedPreferredLanguageField);
            } else {
                modifiedFields.push(field);
            }
        }
        return modifiedFields;
    }

    getPreferredLanguageField(fields: IField[]): IField {
        const preferredLanguageField = fields.find(x => this.isPreferredLanguageField(x));
        return preferredLanguageField;
    }

    isPreferredLanguageField(field: IField): boolean {
        return field.name === 'preferred_language';
    }

    getEmailField(fields: IField[]): IField {
        return fields.find(x => x.name === 'email');
    }

    getNonNullSchemaType(schemaType: string | string[]): string {
        if (typeof schemaType === 'string') {
            return schemaType;
        }

        if (Array.isArray(schemaType)) {
            const typeArr = [...schemaType as string[]];
            // Remove 'null' item from the array
            typeArr.splice(typeArr.indexOf('null'));
            // And return the first from the rest
            return typeArr[0];
        }

        return schemaType;
    }

    // isCriteriaFulfilled(fieldValue: any, criteriaValue: any, operator: FieldOperator): boolean {
    //     const fieldValueIsArray = Array.isArray(fieldValue);
    //     const criteriaValueIsArray = Array.isArray(criteriaValue);
    //     const fieldValueIsString = typeof fieldValue === 'string';
    //     const criteriaValueIsString = typeof criteriaValue === 'string';
    //     if (fieldValueIsString && criteriaValueIsString) {
    //         // Both are strings
    //         const criteriaValueString = (criteriaValue as string) || '';
    //         if (operator === FieldOperator.in) {
    //             // TODO: Probably not used anymore
    //             return criteriaValueString.indexOf(fieldValue) >= 0;
    //         } else if (operator === FieldOperator.notIn) {
    //             // TODO: Probably not used anymore
    //             return criteriaValueString.indexOf(fieldValue) === -1;
    //         } else if (operator === FieldOperator.like) {
    //             return (fieldValue as string).indexOf(criteriaValueString) >= 0;
    //         } else if (operator === FieldOperator.notLike) {
    //             return (fieldValue as string).indexOf(criteriaValueString) === -1;
    //         } else {
    //             // For other string operations use equality comparison
    //             return fieldValue === criteriaValue;
    //         }
    //     } else if (!fieldValueIsArray && criteriaValueIsArray) {
    //         // First is not array and second is array
    //         const criteriaValueArray = criteriaValue as any[];
    //         if (operator === FieldOperator.in) {
    //             return criteriaValueArray.indexOf(fieldValue) >= 0;
    //         } else if (operator === FieldOperator.notIn) {
    //             return criteriaValueArray.indexOf(fieldValue) === -1;
    //         }
    //     } else if (fieldValueIsArray && criteriaValueIsArray) {
    //         // Both are arrays
    //         const fieldValueArray = fieldValue as any[];
    //         const criteriaValueArray = criteriaValue as any[];
    //         if (operator === FieldOperator.in) {
    //             return this.isArrayInArray(fieldValueArray, criteriaValueArray);
    //         } else if (operator === FieldOperator.notIn) {
    //             return !this.isArrayInArray(fieldValueArray, criteriaValueArray);
    //         }
    //     } else {
    //         // Unknown types combination -compare for equality
    //         return fieldValue === criteriaValue;
    //     }

    //     return false;
    // }

    // isArrayInArray(first: any[], second: any[]): boolean {
    //     return first.filter(f => {
    //         return second.includes(f);
    //     }).length > 0;
    // }

    // isValueEmpty(value: any): boolean {
    //     return value === undefined || value === null || value.length === 0;
    // }

    // This is now part of @azavista/advanced-search
    // getDependencyResult(dependency: IDependency, obj: object): boolean {
    //     // In order to satisfy objectMatchesSearch which does not support not_empty
    //     // FieldOperator.empty must become { empty: true }
    //     // FieldOperator.notEmpty must become { empty: false }
    //     // We will make a clone of the criteria so we don't modify the original
    //     const criteria = this.utilsSvc.clone(dependency.criteria);
    //     for (const criteriaItem of criteria) {
    //         if (criteriaItem.operator === FieldOperator.empty) {
    //             criteriaItem.value = true;
    //         } else if (criteriaItem.operator === FieldOperator.notEmpty) {
    //             criteriaItem.operator = FieldOperator.empty;
    //             criteriaItem.value = false;
    //         }
    //     }
    //     const searchParams = flatSearchToRecursive(criteria as FlatSearchParams, 'id');
    //     const result = objectMatchesSearch(obj as any, searchParams);
    //     return result;
    // }

    // getDependencyResultOld(dependency: IDependency, fields: IField[], fieldValues: { fieldId: string, value: any }[]): boolean {
    //     if (!dependency || !dependency.criteria || dependency.criteria.length === 0) {
    //         return false;
    //     }
    //     for (const criteria of dependency.criteria) {
    //         const op = criteria.operator;
    //         const fieldValueIndex = fieldValues.findIndex(x => x.fieldId === criteria.field.id);
    //         if (fieldValueIndex >= 0) {
    //             const fieldValue = fieldValues[fieldValueIndex].value;
    //             const criteriaValue = criteria.value;
    //             if (op === FieldOperator.equal) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.notEqual) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.empty) {
    //                 if (!this.isValueEmpty(fieldValue)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.notEmpty) {
    //                 if (this.isValueEmpty(fieldValue)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.like) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.notLike) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.greaterThan) {
    //                 if (fieldValue <= criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.greaterThanOrEqual) {
    //                 if (fieldValue < criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.lessThan) {
    //                 if (fieldValue >= criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.lessThanOrEqual) {
    //                 if (fieldValue > criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.after) {
    //                 if (fieldValue <= criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.before) {
    //                 if (fieldValue >= criteriaValue) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.in) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             } else if (op === FieldOperator.notIn) {
    //                 if (!this.isCriteriaFulfilled(fieldValue, criteriaValue, op)) {
    //                     return false;
    //                 }
    //             }
    //         }
    //     }

    //     return true;
    // }

    getItemIndex<T>(item: T, arr: T[]): number {
        return arr.indexOf(item);
    }

    prefixWithUrlForResources(path: string): string {
        let urlPrefix = environment.resourcesUrlPrefix || '';
        if (urlPrefix[urlPrefix.length - 1] === '/' && path[0] === '/') {
            // Resources url prefix and provided path have '/' one after the other - remove it from the prefix
            // So we don't end up with paths like https://something.com//theme/theme-id.css  (double // after the domain name)
            // When url prefix ends with / and path starts with /
            urlPrefix = urlPrefix.substr(0, urlPrefix.length - 1);
        }
        return urlPrefix + path;
    }

    getEventIdFromUrl(): string {
        const pathParts = window.location.pathname.split('/');
        const pIndex = pathParts.findIndex(x => x === 'event');
        const eventId = pathParts[pIndex + 1];
        return eventId;
    }

    getAllSearchParams(): IIdWithValue[] {
        const params = new URLSearchParams(window.location.search);
        const result: IIdWithValue[] = [];
        params.forEach((value: string, key: string, parent: URLSearchParams) => {
            result.push({ id: key, value: value });
        });
        return result;
    }

    getSearchParamFromUrl(searchParamName: SearchParamName): string | null {
        const params = new URLSearchParams(window.location.search);
        const pageId = params.get(searchParamName);
        return pageId;
    }

    storeSearchParamInUrl(searchParamName: SearchParamName, value: string): void {
        const url = new URL(window.location.href);
        url.searchParams.set(searchParamName, value || '');
        window.history.pushState(null, null, url.href);
    }

    removeSearchParamFromUrl(searchParamName: SearchParamName): void {
        const url = new URL(window.location.href);
        url.searchParams.delete(searchParamName);
        window.history.replaceState(null, null, url.href);
    }

    isApiErrorInvalidEventToken(err: IErrorNotification): boolean {
        const errCode = err?.error?.communicationError?.body?.code;
        if (errCode === ServerErrorCode.invalidEventToken) {
            return true;
        }
        return false;
    }

    isApiErrorEmailConflict(err: IErrorNotification): boolean {
        const errCode = err?.error?.communicationError?.body?.code;
        if (errCode === ServerErrorCode.emailConflict) {
            return true;
        }
        return false;
    }

    getErrorText(err: IErrorNotification): string {
        if (err.error && err.error.communicationError && err.error.communicationError.body) {
            const errBody = err.error.communicationError.body;
            // TODO: Change IErrorNotification so it contains code and message
            const errBodyAny = errBody as any;
            if (errBody.error || errBody.error_description) {
                return (errBody.error_description || '') + ` (${errBody.error})` + ` (${err.caller})`;
            } else if (errBodyAny.code || errBodyAny.message) {
                let bodyMsg = errBodyAny.message + ` (${errBodyAny.code})`;
                if (errBodyAny.data && errBodyAny.data.errors) {
                    const errorsArr = errBodyAny.data.errors as string[];
                    bodyMsg += ' ' + errorsArr.join('; ');
                }
                return bodyMsg + ` (${err.caller})`;
            }
        }
        let msg = err.caller + ' ' + err.method + ' ' + err.url;
        const commErr = err.error?.communicationError;
        if (commErr) {
            msg += ' ' + commErr.statusText + ' ' + commErr.message;
        }
        const thrownErr = err.error?.thrownError;
        if (thrownErr) {
            msg += ' ' + thrownErr.toString();
        }
        return msg;
    }

    formatPageDataFields(pageDataFields: IObjectWithId, fields: IField[]): Array<IObjectWithId> {
        const pageDataEventObjectKeys = Object.keys(pageDataFields);
        const result = [];
        pageDataEventObjectKeys.forEach(id => {
            const field = fields.find(x => x.id === id);
            if (field) {
                result.push({ id: id, name: field.name });
            }
        });
        return result;
    }

    getRegistrationWorkflowTypes(): string[] {
        return [
            'registration-workflow',
            'public-registration-workflow',
            'payment-workflow'
        ];
    }
}

export const getCartItemFromCartItemProduct = (item: ICartItemProduct): CartItem => ({
    product_id: item.product.id,
    quantity: item.cart_quantity
});
